/**
 * Freigaben je Projekt – wer ein Band-Projekt sehen und bearbeiten darf.
 *
 * Grundsatz: Jedes Projekt hat einen Ersteller (owner). Nur er vergibt
 * Freigaben („Mitarbeiten“ oder „Nur lesen“) und teilt den Speicher unter
 * den Mitgliedern auf. Alles, was zu einem Projekt gehoert (Songs, Setlists,
 * Notizen, Zeichnungen …), erbt dessen Freigabe.
 *
 * In Firestore steht die Freigabe neben jedem Datensatz, damit die Regeln
 * sie ohne Umweg pruefen koennen und eine Abfrage nur liefert, was man sehen
 * darf (firestore.rules, Abschnitt „Band-Daten“):
 *   pid   Projekt, zu dem der Datensatz gehoert (null = persoenlich)
 *   acl   wer lesen darf (Konto-Ids, sortiert)
 *   ed    wer aendern darf (Teilmenge von acl, sortiert)
 *   owner nur bei Projekten: der Ersteller
 *
 * Reine Funktionen ohne Firebase – getestet in tests/sharing.test.ts.
 */
import type { Member, Project } from '../types.ts';

/** Hoechstens so viele Personen je Projekt (auch Grenze in den Regeln). */
export const MAX_MEMBERS = 50;

export interface Access {
  pid: string | null;
  owner?: string;
  acl: string[];
  ed: string[];
}

/** Konto-Ids aus Firebase: Buchstaben und Ziffern, 1–128 Zeichen. */
export function isUid(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9]{1,128}$/.test(value);
}

const sorted = (ids: Iterable<string>): string[] => [...new Set(ids)].sort();

/**
 * Freigabe eines Projekts aus seinen Mitgliedern. Ohne Ersteller (aeltere
 * Projekte vor dem Umbau) gilt, wer es gerade speichert, als Ersteller –
 * die Migration traegt vorher alle richtig ein.
 */
export function projectAccess(project: Pick<Project, 'id' | 'members'>, fallbackOwner: string): Access {
  const members = (project.members ?? []).filter((m) => isUid(m.id)).slice(0, MAX_MEMBERS);
  const owner = members.find((m) => m.role === 'owner')?.id ?? fallbackOwner;
  const acl = sorted([owner, ...members.map((m) => m.id)]);
  const ed = sorted([owner, ...members.filter((m) => m.role === 'editor').map((m) => m.id)]);
  return { pid: project.id, owner, acl, ed };
}

/** Persoenlich: nur man selbst. */
export function personalAccess(uid: string): Access {
  return { pid: null, acl: [uid], ed: [uid] };
}

/** Wo ein Datensatz herkommt – so viel braucht resolveProject() zum Nachschlagen. */
export interface Lookup {
  /** projectId eines Datensatzes aus einer Sammlung, oder undefined. */
  projectOf(store: string, id: string): string | undefined;
}

/**
 * Zu welchem Projekt gehoert ein Datensatz? Songs & Co. tragen projectId;
 * Zeichnungen stecken es in ihrer Kennung (song:, cover:, board:, screen:).
 */
export function resolveProject(store: string, record: { id?: unknown; projectId?: unknown }, lookup: Lookup): string | null {
  if (store === 'projects') return typeof record.id === 'string' ? record.id : null;
  if (typeof record.projectId === 'string') return record.projectId;
  if (store !== 'drawings' || typeof record.id !== 'string') return null;
  const [kind, ...rest] = record.id.split(':');
  const ref = rest.join(':');
  if (kind === 'cover') return (ref.split(':')[0] ?? '') || null;
  if (kind === 'song') return lookup.projectOf('songs', ref) ?? null;
  if (kind === 'board') return lookup.projectOf('boards', ref) ?? null;
  if (kind === 'screen') {
    // z. B. "/note/<id>" oder "/setlist/<id>" – die erste bekannte Kennung zaehlt.
    for (const part of ref.split('/')) {
      for (const s of ['notes', 'setlists', 'songs', 'releases', 'rooms', 'boards']) {
        const pid = lookup.projectOf(s, part);
        if (pid) return pid;
      }
    }
  }
  return null;
}

/** Gleiche Freigabe? (Reihenfolge ist durch sorted() fest.) */
export function sameAccess(a: Access | undefined, b: Access): boolean {
  return !!a && a.pid === b.pid && a.owner === b.owner && a.acl.join() === b.acl.join() && a.ed.join() === b.ed.join();
}

/* ------------------------------------------------------ Speicher-Anteile */

/**
 * Anteile am Projekt-Speicher in Prozent je Mitglied. Frei einstellbar;
 * hier nur auf ganze Zahlen gebracht, die zusammen genau 100 ergeben.
 * Wer nicht (mehr) Mitglied ist, faellt heraus; fehlt alles, traegt der
 * Ersteller 100 %.
 */
export function normalizeShares(shares: Record<string, number> | undefined, acl: string[], owner: string): Record<string, number> {
  const entries = acl.map((id) => [id, Math.max(0, Number(shares?.[id]) || 0)] as const);
  const total = entries.reduce((n, [, v]) => n + v, 0);
  if (total <= 0) return Object.fromEntries(acl.map((id) => [id, id === owner ? 100 : 0]));
  // Groesste-Reste-Verfahren: ganze Prozente, Summe exakt 100.
  const raw = entries.map(([id, v]) => ({ id, exact: (v / total) * 100 }));
  const out = raw.map((r) => ({ id: r.id, n: Math.floor(r.exact), rest: r.exact - Math.floor(r.exact) }));
  let missing = 100 - out.reduce((n, r) => n + r.n, 0);
  for (const r of [...out].sort((a, b) => b.rest - a.rest)) {
    if (missing <= 0) break;
    r.n += 1;
    missing -= 1;
  }
  return Object.fromEntries(out.map((r) => [r.id, r.n]));
}

/** Gleich verteilt – der Vorschlag beim Teilen. */
export function evenShares(acl: string[], owner: string): Record<string, number> {
  return normalizeShares(Object.fromEntries(acl.map((id) => [id, 1])), acl, owner);
}

/** Wie viele Bytes jemand zu einem Projekt beitraegt. */
export function contribution(bytes: number, shares: Record<string, number>, uid: string): number {
  return Math.round((bytes * (shares[uid] ?? 0)) / 100);
}

/** Mitglied mit Rolle – fuer die Oberflaeche. */
export function memberRole(project: Pick<Project, 'members'>, uid: string): Member['role'] | null {
  return project.members?.find((m) => m.id === uid)?.role ?? null;
}
